/**
 * Sales_Cycle definitions and assignment: `/api/sales-cycles`,
 * `/api/sales-cycles/:id/impact`, `/api/sales-cycles/:id/apply` and
 * `/api/sales-cycle-derivation` (Requirements 6, 7).
 *
 * Changing a cycle's dates is deliberately a two-call operation, and the schemas
 * are what make it one: `impact` computes the affected Initiatives without
 * persisting anything (R6.7), and `apply` persists the definition and reassigns
 * **only** the Initiatives the user selected (R6.8, R6.9). A single-call design
 * would have to guess, and R6.8 forbids a definition change from silently moving an
 * assignment.
 *
 * Two warnings gate a definition change - overlap (R6.4) and gap (R6.5) - and
 * nothing persists unless they are acknowledged, so `apply` carries the
 * acknowledged rule identifiers. Derivation (`/api/sales-cycle-derivation`) is a
 * read: R7.1 recomputes the set on date entry without a save.
 */
import { z } from 'zod';
import { assignmentSourceSchema } from './enums.js';
import { IMPACT_PAGE_SIZE, MIN_MANUAL_SALES_CYCLES_PER_YEAR, SALES_CYCLE_NAME_MAX_LENGTH, SALES_CYCLE_NAME_MIN_LENGTH, } from './limits.js';
import { booleanFlagSchema, idSchema, initiativeRefSchema, justificationSchema, pagedResponseSchema, paginationQuerySchema, planningDateSchema, planningYearSchema, ruleIdSchema, textSchema, versionSchema, } from './primitives.js';
/** `/api/sales-cycles/:id`. */
export const salesCycleIdParamsSchema = z.object({ id: idSchema });
/** A Sales_Cycle definition as read back (R6.1, R7.2). */
export const salesCycleSchema = z.object({
    id: idSchema,
    planningYear: planningYearSchema,
    name: textSchema(SALES_CYCLE_NAME_MIN_LENGTH, SALES_CYCLE_NAME_MAX_LENGTH),
    startDate: planningDateSchema,
    endDate: planningDateSchema,
    effectiveFrom: planningDateSchema,
    isActive: z.boolean(),
});
/**
 * `GET /api/sales-cycles`. Definitions by year.
 *
 * Inactive cycles are excluded by default because R6.6 keeps them out of new
 * assignment proposals, and included on request because an Initiative that
 * already references one must still render it (R5.9).
 */
export const salesCycleListQuerySchema = z.object({
    planningYear: planningYearSchema.nullable().default(null),
    // A query-string flag, so it must survive arriving as the text `'true'`;
    // `booleanFlagSchema` accepts the two literal spellings and a real boolean,
    // where a bare `z.boolean()` would reject the `?includeInactive=true` a client
    // actually sends.
    includeInactive: booleanFlagSchema.default(false),
});
/** The definitions of the requested years, in date order. */
export const salesCycleListResponseSchema = z.object({
    items: z.array(salesCycleSchema).default([]),
});
/**
 * The Sales_Cycle_Timing_Form payload (R6.1).
 *
 * Per-year name uniqueness (R6.2) and `end_date > start_date` (R6.3) are rules with
 * identifiers and messages, evaluated by the Validation_Engine and backed by
 * database constraints; the schema states the field shapes and the 1-to-50
 * character name limit.
 */
export const salesCycleWriteSchema = z.object({
    planningYear: planningYearSchema,
    name: textSchema(SALES_CYCLE_NAME_MIN_LENGTH, SALES_CYCLE_NAME_MAX_LENGTH),
    startDate: planningDateSchema,
    endDate: planningDateSchema,
    effectiveFrom: planningDateSchema,
    isActive: z.boolean().default(true),
});
/**
 * `POST /api/sales-cycles`, and `PUT /api/sales-cycles/:id` for a change that
 * moves no date: the definition plus the overlap and gap warnings of R6.4 and R6.5
 * acknowledged in the same call, without which nothing persists.
 *
 * A change that *does* move a date goes through impact and apply instead, because
 * R6.7 requires the impact list to be shown before anything is persisted. Both
 * answer with a {@link salesCycleSchema}.
 */
export const createSalesCycleRequestSchema = salesCycleWriteSchema.extend({
    acknowledgedWarningRuleIds: z.array(ruleIdSchema).default([]),
});
/**
 * `POST /api/sales-cycles/:id/impact`. The impact list of a *proposed* definition
 * change, computed and shown before anything is persisted (R6.7).
 *
 * Paginated at 200 rows with a total count, because this is the one Foundation read
 * that can touch every Initiative of a year.
 */
export const salesCycleImpactRequestSchema = salesCycleWriteSchema
    .pick({ startDate: true, endDate: true })
    .merge(paginationQuerySchema(IMPACT_PAGE_SIZE));
/**
 * One affected Initiative: one whose proposed assignment set differs from its
 * current set (R6.7).
 *
 * `requiresExplicitSelection` carries R6.10: an Initiative with a manual override
 * or in status Approved or Final is excluded from the default selection and must be
 * chosen individually, and its override reason is retained when it is not.
 */
export const salesCycleImpactItemSchema = z.object({
    initiativeId: idSchema,
    initiativeRef: initiativeRefSchema,
    title: z.string(),
    currentSalesCycleIds: z.array(idSchema).default([]),
    proposedSalesCycleIds: z.array(idSchema).default([]),
    currentAssignmentSource: assignmentSourceSchema,
    requiresExplicitSelection: z.boolean(),
});
/** A page of the impact list, with the count of affected Initiatives (R6.7). */
export const salesCycleImpactResponseSchema = pagedResponseSchema(salesCycleImpactItemSchema);
/**
 * `POST /api/sales-cycles/:id/apply`. Persists the definition and reassigns only
 * the listed Initiatives the user selected (R6.8, R6.9).
 *
 * An empty `reassignInitiativeIds` is a legitimate request: the definition changes
 * and every assignment stays as it is.
 */
export const applySalesCycleRequestSchema = salesCycleWriteSchema.extend({
    reassignInitiativeIds: z.array(idSchema).default([]),
    acknowledgedWarningRuleIds: z.array(ruleIdSchema).default([]),
});
/**
 * The persisted definition and how many Initiatives were reassigned - the count
 * R6.11 records with the Change_Event.
 */
export const applySalesCycleResponseSchema = z.object({
    salesCycle: salesCycleSchema,
    reassignedInitiativeCount: z.number().int().min(0),
});
/**
 * `POST /api/sales-cycle-derivation`. The derived set for a date range, with no
 * save (R7.1).
 *
 * A read expressed as a POST because the input is a date range rather than a
 * resource identifier; it persists nothing and is capability-gated as a read.
 */
export const salesCycleDerivationRequestSchema = z.object({
    startDate: planningDateSchema,
    endDate: planningDateSchema,
});
/**
 * The derived set, carrying for each cycle the year, name and the dates the
 * proposal was derived from (R7.2), plus the years the range intersects so the
 * form can state the scope of the derivation.
 */
export const salesCycleDerivationResponseSchema = z.object({
    intersectedPlanningYears: z.array(planningYearSchema).default([]),
    derivedSalesCycles: z.array(salesCycleSchema).default([]),
});
/**
 * `POST /api/initiatives/:id/sales-cycle-override`. A manual Sales_Cycle
 * assignment that replaces the derived set with a user-chosen one (R7.4).
 *
 * The set is at least one identifier (`MIN_MANUAL_SALES_CYCLES_PER_YEAR`, the
 * same floor the discriminated `manual` assignment on the intake form uses); the
 * 1-to-12-per-intersected-year ceiling and the active/planning-year rules are the
 * Validation_Engine's, evaluated against the loaded definitions rather than the
 * schema (R7.4, R7.5). The reason is the 10-to-500-character `justificationSchema`
 * migration 0004's `manual_override_requires_reason` makes structural. `version`
 * is the record version the client last saw, so a stale override is a `409`
 * rather than a silent overwrite (R5.8). The actor and timestamp of the override
 * are the request's Principal and the server clock, never client input (R7.4).
 */
export const manualSalesCycleOverrideRequestSchema = z.object({
    selectedSalesCycleIds: z.array(idSchema).min(MIN_MANUAL_SALES_CYCLES_PER_YEAR),
    reason: justificationSchema,
    version: versionSchema,
});
/**
 * The stored Sales_Cycle assignment of one Initiative, as read back by the
 * manual-override endpoint (R7.6, R7.7).
 *
 * Carries the effective assigned set (the manual set while an override is in
 * force, R7.7), the assignment source, the override reason/actor/timestamp when
 * the source is `manual_override`, and - for the Detail_Panel comparison of R7.6
 * - the set derivation would produce for the Initiative's current dates. The
 * `version` is the record version after the operation, so the client can carry it
 * into the next write.
 *
 * Named `...OverrideResponse` to sit beside `salesCycleAssignmentSchema` in
 * `initiative.ts` (the Detail_Panel read of R7.6) rather than collide with it:
 * this one is the write endpoint's acknowledgement of the stored assignment,
 * carrying the record version the next write needs.
 */
export const manualSalesCycleOverrideResponseSchema = z.object({
    initiativeId: idSchema,
    version: versionSchema,
    assignmentSource: assignmentSourceSchema,
    assignedSalesCycleIds: z.array(idSchema).default([]),
    overrideReason: z.string().nullable().default(null),
    overrideByUserId: idSchema.nullable().default(null),
    overrideAt: z.string().nullable().default(null),
    intersectedPlanningYears: z.array(planningYearSchema).default([]),
    derivedSalesCycleIds: z.array(idSchema).default([]),
});
/**
 * `GET /api/initiatives/:id/sales-cycle-override/divergence`. The divergence of
 * a stored manual override from the set derivation would produce for the
 * Initiative's current dates (R7.6, R7.7, R7.8).
 *
 * A read: it recomputes the derived set for display only and never mutates the
 * stored assignment (R7.7). `requiresAcknowledgement` states whether R7.8's
 * warning applies - true exactly when a manual override is in force and the
 * stored set no longer equals the recomputed derived set - and the two lists the
 * warning must name (`nonIntersectingSalesCycleIds`, `derivedSalesCycleIds`) are
 * carried so the client can render it and then complete the date change by
 * acknowledging.
 */
export const salesCycleDivergenceResponseSchema = z.object({
    initiativeId: idSchema,
    assignmentSource: assignmentSourceSchema,
    isManualOverride: z.boolean(),
    assignedSalesCycleIds: z.array(idSchema).default([]),
    derivedSalesCycleIds: z.array(idSchema).default([]),
    addedByDerivationSalesCycleIds: z.array(idSchema).default([]),
    removedByDerivationSalesCycleIds: z.array(idSchema).default([]),
    nonIntersectingSalesCycleIds: z.array(idSchema).default([]),
    deactivatedSalesCycleIds: z.array(idSchema).default([]),
    setsAgree: z.boolean(),
    requiresAcknowledgement: z.boolean(),
});
//# sourceMappingURL=sales-cycle.js.map